import { Box, Spinner, Stack, Text } from '@chakra-ui/react';

import { Button } from '@m/components-ui/button';
import { CheckCircle } from '@m/icons';
import {
  PAYMENT_ALREADY_RECEIVED_MESSAGE,
  PAYMENT_IN_PROGRESS_MESSAGE,
} from '@m/utils/duplicatePaymentGuard';

export type PaymentOutcomeKind = 'in_progress' | 'already_paid';

interface PaymentOutcomeNoticeProps {
  kind: PaymentOutcomeKind;
  onContinue?: () => void;
  /** `in_progress` only: the visit has not flipped to paid within the polling window. */
  timedOut?: boolean;
}

const IN_PROGRESS_BODY =
  "A payment for this visit is already being processed. Please don't pay again.";
const IN_PROGRESS_TIMED_OUT_BODY =
  'This is taking longer than expected. Please wait a moment and refresh this page before trying again.';
const ALREADY_PAID_BODY = "This visit has already been paid. You're all set to drive out.";

/**
 * Terminal state shown in place of the pay controls once the server has refused a second payment
 * (claim lock) or the visit turned out to be paid already. Replaces, never sits next to, a Pay
 * button: the whole point is that the customer stops here.
 */
export function PaymentOutcomeNotice({
  kind,
  onContinue,
  timedOut = false,
}: PaymentOutcomeNoticeProps) {
  const isInProgress = kind === 'in_progress';

  let body = ALREADY_PAID_BODY;
  if (isInProgress) {
    body = timedOut ? IN_PROGRESS_TIMED_OUT_BODY : IN_PROGRESS_BODY;
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      width="100%"
      mt={4}
      px={4}
      py={6}
      borderWidth="1px"
      borderColor="stroke.subtle"
      borderRadius="md"
      bg="fill.surface"
    >
      <Stack gap={3} align="center" textAlign="center">
        {isInProgress ? (
          <Spinner size="lg" color="text.primary" />
        ) : (
          <Box fontSize="4xl" lineHeight="1" color="text.primary">
            <CheckCircle />
          </Box>
        )}
        <Text variant="Subheader" color="text.primary">
          {isInProgress ? PAYMENT_IN_PROGRESS_MESSAGE : PAYMENT_ALREADY_RECEIVED_MESSAGE}
        </Text>
        <Text variant="MainBodyRegular" color="text.secondary">
          {body}
        </Text>
        {!isInProgress && onContinue && (
          <Button variant="brand" size="lg" width="100%" mt={2} onClick={onContinue}>
            Continue
          </Button>
        )}
      </Stack>
    </Box>
  );
}
